// Rapidry print system: professional A4 invoice + thermal QR labels (Order +
// Service Group), sized to the 50mm taffeta/thermal roll spec. All QR uses
// error-correction Q + a 4-module quiet zone (see orderQr.QR_OPTIONS).
import { buildScanText, svgQrDataUrl, type QrPayload } from './orderQr'

export type PrintItem = {
  name: string
  quantity: number
  item_count?: number | null
  unit?: string | null
  unit_price?: number
  total_price?: number
}

export type ServiceGroup = {
  group_id: string
  code?: string | null
  label: string
  service_slug?: string | null
  delivery_model?: string
  item_count: number
  status?: string
  qr_data_url: string | null
  items: PrintItem[]
}

// A label must NEVER print without a service name. `label` should already hold
// the service name, but guard against a blank/whitespace value (legacy group
// rows, a service saved with an empty name) by falling back to a humanized
// slug, then a generic word - so staff always see what the bag is.
const humanizeSlug = (slug?: string | null): string =>
  String(slug || '')
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim()

export const groupServiceName = (g: Pick<ServiceGroup, 'label' | 'service_slug'>): string => {
  const label = String(g.label || '').trim()
  if (label) return label
  return humanizeSlug(g.service_slug) || 'Service'
}

export type PrintOrderMeta = {
  invoiceNumber?: string | null
  orderNumber: string
  displayId?: string
  customerName: string
  customerPhone?: string
  customerEmail?: string | null
  customerId?: string | null
  pickupAddress?: string | null
  deliveryAddress?: string | null
  createdAt?: string | null
  pickupDate?: string | null
  pickupSlot?: string | null
  subtotal?: number
  deliveryFee?: number
  discount?: number
  total?: number
  calculatedPayableAmount?: number | null
  finalAmountPaid?: number | null
  manualAdjustmentAmount?: number | null
  manualAdjustmentRemark?: string | null
  paymentStatus?: string | null
  paymentMode?: string | null
  transactionId?: string | null
  paidAt?: string | null
  qrPayload?: QrPayload | null
  qrToken?: string | null
  supportPhone?: string
  website?: string
}


const esc = (s: unknown) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string
  ))

// item_count / quantity are now DECIMAL(8,2) columns (weight support), so a
// count of 1 arrives from Postgres as the string "1.00". Show whole numbers
// with NO decimals ("1 item"), and only surface a fraction when one genuinely
// exists ("1.5 items"). Trailing zeros are trimmed: 1.00→1, 3.50→3.5.
const fmtCount = (n: unknown): string => {
  const num = Number(n)
  if (!Number.isFinite(num)) return '0'
  return String(Number(num.toFixed(2)))
}
const itemWord = (n: unknown): string => (Number(n) === 1 ? 'item' : 'items')

const fmtDate = (iso?: string | null) => {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

const orderQrDataUrl = async (meta: PrintOrderMeta, dotsPerModule = 5): Promise<string> => {
  // Minimal payload (orderId/token only) so the printed QR stays a low version
  // (v5) with big, thermal-scannable modules. Pixel-exact PNG at an integer
  // dots/module - SVG printed fuzzy on the 1-bit TSC TTP-244 head.
  const text = buildScanText({
    qrPayload: meta.qrPayload,
    qrToken: meta.qrToken,
  })
  return svgQrDataUrl(text || '', dotsPerModule)
}

// Exported so the B2B room receipt/tag sheet (features/b2b/print) uses the same
// popup-then-hidden-iframe fallback as every other Rapidry print job.
export const openPrintWindow = (title: string, head: string, body: string) => {
  const html = `<html><head><meta charset="utf-8"/><title>${esc(title)}</title>${head}</head><body onload="window.print()">${body}</body></html>`
  const win = window.open('', '_blank', 'noopener,noreferrer,width=900,height=1000')
  if (win) {
    win.document.write(html)
    win.document.close()
    return
  }
  // Popup blocked - print via a hidden iframe.
  const iframe = document.createElement('iframe')
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0'
  document.body.appendChild(iframe)
  const doc = iframe.contentWindow?.document
  if (doc) {
    doc.open()
    doc.write(html)
    doc.close()
    iframe.contentWindow?.focus()
    setTimeout(() => {
      iframe.contentWindow?.print()
      setTimeout(() => document.body.removeChild(iframe), 1500)
    }, 250)
  }
}

// ---------------------------------------------------------------------------
// PART 1/2 - Full invoice (A4)
// ---------------------------------------------------------------------------
// The invoice is NOT built here any more.
//
// It used to be assembled as HTML in this file, which meant the admin print
// output was a second implementation of the invoice that drifted from the one
// customers received. Every invoice now comes from @rapidry/invoice via
// GET /admin/orders/:id/invoice, so admin print, the customer download, the
// WhatsApp attachment and the email attachment are the same bytes.
//
// See docs/INVOICE_DESIGN.md. Do not add invoice markup to this file - CI
// fails the build if it reappears.
// ---------------------------------------------------------------------------
// PART 3 - Order QR label (thermal, 50mm × 30mm), one per order, at pickup
// ---------------------------------------------------------------------------
export const openOrderLabel = async (meta: PrintOrderMeta, totalItems?: number, copies = 1) => {
  // VERTICAL (portrait) layout - matches the service-bag label. The QR sits
  // dead-centre in a single stacked column, so it can never drift off the right
  // edge of the 50mm thermal roll (the old side-by-side layout put the QR in a
  // right-hand column that clipped on the roll). 5 dots/module → 225px → 28.15mm
  // (1px == 1 printer dot) keeps it crisp and centred.
  const qr = await orderQrDataUrl(meta, 5)
  // Number of physical labels to print for this one order. Each page is
  // identical (same QR / order id) except a "i/N" sequence badge so staff can
  // match every garment bag during sorting → delivery. N=1 prints "1/1".
  const labelCount = Math.max(1, Math.floor(Number(copies)) || 1)
  const head = `<style>
    /* ROOT-CAUSE FIX (right-shift / clipping on the TSC TTP-244):
       The TTP-244 has a 104mm (4") print head; the 50mm label is loaded
       LEFT-justified under it (dots 0-400). The Windows driver lays the page
       out across the FULL head width, NOT our 50mm @page. Anchor a FIXED
       media-width box (50mm) at the page origin; the flex column then centres
       the QR WITHIN the 50mm (never centring the box across the head), so the
       QR always prints on the label. (NOT a hardcoded offset.) */
    @page { size: 50mm 70mm; margin: 0; }
    * { box-sizing: border-box; }
    html, body {
      margin: 0; padding: 0; width: 50mm; font-family: Arial, sans-serif;
      color: #000; background: #fff;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
      -webkit-font-smoothing: none;
    }
    /* Fixed 50mm box, left-anchored at the origin; content stacked + centred
       within it. overflow:hidden keeps a long line from spilling to a 2nd page. */
    .lbl {
      width: 50mm; height: 70mm; padding: 2mm;
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      gap: 0.6mm; text-align: center; overflow: hidden;
      page-break-after: always; break-after: page;
    }
    .lbl:last-child { page-break-after: auto; break-after: auto; }
    .brand { font-size: 8pt; font-weight: 800; letter-spacing: .5px; }
    .oid { font-size: 15pt; font-weight: 800; line-height: 1.1; max-width: 100%;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    /* Sequence badge (i/N) - prominent, directly beneath the order id, above the
       QR. Smaller than the order id so it doesn't dominate. */
    .seq { font-size: 13pt; font-weight: 800; line-height: 1; margin: 0.4mm 0; }
    /* 225px PNG (5 dots/module) at exactly 225÷8 = 28.15mm so 1px == 1 printer
       dot; pixelated/crisp-edges stops the browser re-interpolating. Centred. */
    .qr { width: 28.15mm; height: 28.15mm; display: block; margin: 0 auto;
      image-rendering: pixelated; image-rendering: crisp-edges; }
    .row { font-size: 8pt; font-weight: 700; line-height: 1.2; max-width: 100%;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .code { font-family: 'Courier New', monospace; font-size: 6pt; text-align: center; }
  </style>`
  // ONE order ID everywhere: the order_number (RD-####) that the dashboard,
  // customer app and QR all reference. The daily display_id (RP-YYMMDD-####) is
  // internal-only and no longer printed, so staff never see two IDs for one bag.
  // Each label is identical except its "i/N" sequence badge.
  const oneLabel = (seq: number) => `
    <div class="lbl">
      <div class="brand">RAPIDRY</div>
      <div class="oid">${esc(meta.orderNumber)}</div>
      <div class="seq">${seq}/${labelCount}</div>
      ${qr ? `<img class="qr" src="${qr}" alt="QR"/>` : ''}
      <div class="row"><strong>${esc(meta.customerName)}</strong></div>
      ${meta.customerPhone ? `<div class="row">${esc(meta.customerPhone)}</div>` : ''}
      ${meta.pickupDate ? `<div class="row">${esc(fmtDate(meta.pickupDate))}</div>` : ''}
      <div class="row"><strong>${totalItems != null ? `${fmtCount(totalItems)} ${Number(totalItems) === 1 ? 'Item' : 'Items'}` : ''}</strong></div>
      <div class="code">${esc(meta.orderNumber)}</div>
    </div>`
  const body = Array.from({ length: labelCount }, (_, i) => oneLabel(i + 1)).join('')
  openPrintWindow('Order label', head, body)
}

// ---------------------------------------------------------------------------
// PART 4 - Service group labels (thermal, 50mm × 25mm), one per service bag
// ---------------------------------------------------------------------------
// Vertical layout for the 30mm continuous garment tape (matches PrintLabel.tsx).
// Each per-service bag gets one tag: SERVICE NAME on top, group code, QR, then
// the full item breakdown + date down the tape.
const SERVICE_LABEL_HEAD = `<style>
    /* ROOT-CAUSE FIX (right-shift / clipping on the TSC TTP-244):
       The TTP-244 has a 104mm (4") print head; the 50mm label is loaded
       LEFT-justified under it (dots 0-400). The Windows driver lays the page
       out across the FULL head width, NOT our 50mm @page - so a width:100% box
       that centres its content lands the QR at ~52mm, past the right edge of a
       50mm label (left blank, right clipped). Browser preview centres within
       the 50mm @page, so it looks correct: that's the preview≠print mismatch.
       Fix: anchor a FIXED media-width box (50mm) at the page origin and do NOT
       centre the box; content then prints at dots 0-400 regardless of the width
       the driver negotiates. height:100vh keeps one bag = one segment (no QR
       split). (NOT a hardcoded offset - it removes page-width dependence.) */
    @page { size: 50mm 70mm; margin: 0; }
    * { box-sizing: border-box; }
    html, body {
      margin: 0; padding: 0; width: 50mm; font-family: Arial, sans-serif;
      color: #000000; background: #fff; -webkit-font-smoothing: none;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    /* Fixed 50mm box, left-anchored at the page origin. The flex stack centres
       content WITHIN the 50mm (QR sits dead-centre on the label); the box itself
       is never centred across the head, so it can't drift right. overflow:hidden
       keeps a long item list from pushing onto a 2nd segment. */
    .lbl {
      width: 50mm; height: 70mm; padding: 2mm 2mm;
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      gap: 0.6mm; text-align: center; overflow: hidden;
      page-break-after: always; break-after: page;
    }
    .lbl:last-child { page-break-after: auto; break-after: auto; }
    /* Service name: up to 2 lines then ellipsize. */
    .svc {
      font-size: 12px; font-weight: 800; line-height: 1.1; color: #000;
      display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
      overflow: hidden; word-break: break-word; max-width: 100%;
    }
    .code {
      font-size: 13px; font-weight: 800; letter-spacing: 0; line-height: 1.1;
      color: #000; max-width: 100%;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    /* Sequence badge (Bag i/N) - each service bag in an order is a distinct
       physical bag, so this counts them 1..N. Prominent but under the code so
       it doesn't crowd the QR. */
    .bagseq { font-size: 12px; font-weight: 800; line-height: 1; color: #000; }
    .qr {
      /* 225px PNG (5 dots/module, pinned v5 = 45 modules) shown at exactly
         225÷8 = 28.15mm so 1 image px == 1 printer dot and every module edge
         lands on the dot grid. pixelated/crisp-edges = no browser interpolation;
         this is what makes the printed QR as sharp as the on-screen one. */
      width: 28.15mm; height: 28.15mm; display: block; margin: 0 auto;
      image-rendering: pixelated; image-rendering: crisp-edges;
    }
    .noqr { width: 28.15mm; height: 28.15mm; display: grid; place-items: center; border: 1px dashed #ccc; font-size: 7px; color: #999; }
    .row {
      font-size: 10px; font-weight: 700; line-height: 1.2; color: #000;
      max-width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    /* Items breakdown - the only line that grows unbounded with order size.
       Clamp at 3 lines so the whole label fits within a ~58mm stock segment
       (QR + name + code + count + date + 3 item lines ≈ 53mm). Prevents the
       overflow that split the label across 2 segments and cut the QR. */
    .items {
      font-size: 9px; font-weight: 700; line-height: 1.2; color: #000;
      word-break: break-word; max-width: 100%;
      display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical;
      overflow: hidden; text-overflow: ellipsis;
    }
  </style>`

// One thermal bag sticker for a single service group. `seq`/`total` render the
// "Bag i/N" badge - the ONLY element that changes between a group's copies. The
// service name is always on top (never printed without one; see groupServiceName).
const oneServiceCard = (meta: PrintOrderMeta, g: ServiceGroup, seq: number, total: number): string => {
  const express = String(g.delivery_model).toLowerCase() === 'express' ? ' ⚡' : ''
  const items = (g.items || []).map((it) => `${fmtCount(it.quantity)}×${esc(it.name)}`).join(', ')
  const qr = g.qr_data_url ? `<img class="qr" src="${g.qr_data_url}" alt="QR"/>` : '<div class="noqr">no QR</div>'
  return `
      <div class="lbl">
        <div class="svc">${esc(groupServiceName(g))}${express}</div>
        <div class="code">${esc(g.code || '')}</div>
        <div class="bagseq">Bag ${seq}/${total}</div>
        ${qr}
        <div class="row">${esc(meta.orderNumber)} · ${fmtCount(g.item_count)} ${itemWord(g.item_count)}</div>
        ${meta.customerName ? `<div class="row">${esc(meta.customerName)}</div>` : ''}
        <div class="items">${items || ''}</div>
        <div class="row">${esc(fmtDate(meta.createdAt))}</div>
      </div>`
}

// One sticker per service bag in the order (Bag i/<#groups>) - the quick "print
// all groups" run used by the Service Groups section.
const serviceLabelCards = (meta: PrintOrderMeta, groups: ServiceGroup[]): string =>
  groups.map((g, i) => oneServiceCard(meta, g, i + 1, groups.length)).join('')

export const openServiceLabels = (meta: PrintOrderMeta, groups: ServiceGroup[]) => {
  openPrintWindow('Service labels', SERVICE_LABEL_HEAD, serviceLabelCards(meta, groups))
}

// One service group's bag labels as HTML: `copies` cards numbered Bag 1/N … N/N.
const serviceBatchCards = (meta: PrintOrderMeta, group: ServiceGroup, copies: number): string => {
  const n = Math.max(1, Math.min(200, Math.floor(Number(copies)) || 1))
  return Array.from({ length: n }, (_, i) => oneServiceCard(meta, group, i + 1, n)).join('')
}

// Print `copies` bag labels for ONE service group, numbered Bag 1/N … Bag N/N.
// Laundry ops are organized per service (different teams/stations), so each
// service gets its own batch with its own count instead of one order-wide qty.
export const openServiceGroupLabels = (meta: PrintOrderMeta, group: ServiceGroup, copies: number) => {
  openPrintWindow('Service labels', SERVICE_LABEL_HEAD, serviceBatchCards(meta, group, copies))
}

// Print MANY service batches in ONE print job - the "Print Selected / Print All"
// path. Each entry keeps its own Bag i/N sequence (restarting per service), so
// one dialog yields e.g. Wash&Fold 1/5…5/5 then Steam Iron 1/3…3/3, back to back.
export const openServiceGroupBatches = (
  meta: PrintOrderMeta,
  batches: { group: ServiceGroup; copies: number }[],
) => {
  const body = batches.map(({ group, copies }) => serviceBatchCards(meta, group, copies)).join('')
  openPrintWindow('Service labels', SERVICE_LABEL_HEAD, body)
}

// Bulk: one print run across MANY orders, each with its own meta + groups.
export const openServiceLabelsMulti = (entries: { meta: PrintOrderMeta; groups: ServiceGroup[] }[]) => {
  const body = entries.map((e) => serviceLabelCards(e.meta, e.groups)).join('')
  openPrintWindow('Service labels', SERVICE_LABEL_HEAD, body)
}
